<template>
  <div class="rp">
    <header class="rp-hero">
      <div class="rp-hero__glow" aria-hidden="true"></div>
      <div class="rp-hero__text">
        <span class="rp-eyebrow">ALT GROUP · управление системой</span>
        <h1>Пульт правил</h1>
        <p>
          Сроки, границы отмен и переносов, проверки — всё, по чему работает система, в одном месте.
          Изменение вступает в силу в течение минуты, каждое попадает в историю: кто, когда и почему.
        </p>
      </div>
      <div class="rp-stats">
        <div class="rp-stat"><b>{{ total }}</b><span>правил</span></div>
        <div class="rp-stat"><b>{{ liveCount }}</b><span>действуют</span></div>
        <div class="rp-stat"><b>{{ changedCount }}</b><span>изменены</span></div>
      </div>
    </header>

    <div class="rp-toolbar">
      <label class="rp-search">
        <span class="rp-search__icon" aria-hidden="true">⌕</span>
        <input v-model="query" type="search" placeholder="Найти правило: «отмена», «16:00», «Торг Транс»…" />
      </label>
      <nav class="rp-tabs">
        <button v-for="g in groups" :key="g.key" class="rp-tab" @click="scrollTo(g.key)">{{ g.title }}</button>
      </nav>
      <button class="rp-btn rp-btn--ghost" :disabled="loading" @click="load">Обновить</button>
    </div>

    <p class="rp-legend">
      <span class="chip chip--live">Действует</span> система уже работает по значению ·
      <span class="chip chip--planned">Применится при запуске модуля</span> можно задать заранее ·
      <span class="chip chip--fixed">Пока задано в программе</span> показано для полноты
    </p>

    <p v-if="error" class="rp-error">{{ error }}</p>
    <div v-else-if="loading && !groups.length" class="rp-skeleton">
      <div v-for="n in 4" :key="n" class="rp-skeleton__row"></div>
    </div>
    <p v-else-if="query && !shown.length" class="rp-muted rp-empty">По запросу «{{ query }}» правил нет.</p>

    <section v-for="group in shown" :key="group.key" :id="'rules-' + group.key" class="rp-group">
      <h2>{{ group.title }}</h2>
      <transition-group name="rp-card" tag="div" class="rp-cards">
        <article v-for="rule in group.rules" :key="rule.key" class="rp-card"
                 :class="{ 'rp-card--changed': !rule.is_default, 'rp-card--open': editing === rule.key }">
          <div class="rp-card__main">
            <div class="rp-card__title">
              {{ rule.title }}
              <span class="chip" :class="'chip--' + tone(rule.status)">{{ rule.status_title }}</span>
            </div>
            <p class="rp-card__text">{{ rule.description }}</p>
            <p v-if="rule.where" class="rp-note"><span>Где действует</span>{{ rule.where }}</p>
            <p v-if="rule.decided" class="rp-note"><span>Решение</span>{{ rule.decided }}</p>
            <p v-if="rule.updated_by" class="rp-note">
              <span>Изменено</span>{{ rule.updated_by }}, {{ changedAt(rule.updated_at) }}
            </p>
          </div>

          <div class="rp-card__side">
            <div class="rp-value">{{ rule.value_text }}</div>
            <div v-if="!rule.is_default" class="rp-default">по умолчанию {{ rule.default_text }}</div>

            <div v-if="canEdit && rule.editable && editing !== rule.key" class="rp-actions">
              <button class="rp-btn" @click="startEdit(rule)">Изменить</button>
              <button v-if="!rule.is_default" class="rp-btn rp-btn--ghost" :disabled="saving" @click="reset(rule)">
                Вернуть {{ rule.default_text }}
              </button>
            </div>
            <p v-else-if="!rule.editable" class="rp-muted rp-small">Меняется по плану развития системы</p>

            <button v-if="rule.changes" class="rp-link" @click="toggleHistory(rule)">
              {{ historyOpen === rule.key ? 'Скрыть историю' : 'История изменений · ' + rule.changes }}
            </button>
          </div>

          <transition name="rp-slide">
            <form v-if="editing === rule.key" class="rp-form" @submit.prevent="save(rule)">
              <label>
                Новое значение <span class="rp-muted">({{ range(rule) }})</span>
                <select v-if="rule.unit === 'hour'" v-model="draft.value">
                  <option v-for="o in hours(rule)" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <input v-else v-model="draft.value" type="number" :min="rule.minimum" :max="rule.maximum" step="1" />
              </label>
              <label>
                Почему меняем <span class="rp-muted">(попадёт в историю)</span>
                <textarea v-model="draft.comment" rows="2" maxlength="500"
                          placeholder="Например: Эфес сдвинул окно отмены до 17:00 с 01.12"></textarea>
              </label>
              <p v-if="formError" class="rp-error">{{ formError }}</p>
              <div class="rp-actions">
                <button class="rp-btn" type="submit" :disabled="saving">Сохранить</button>
                <button class="rp-btn rp-btn--ghost" type="button" @click="cancelEdit">Отмена</button>
              </div>
            </form>
          </transition>

          <transition name="rp-slide">
            <ul v-if="historyOpen === rule.key" class="rp-history">
              <li v-for="(h, i) in history" :key="i">
                <b>{{ changedAt(h.at) }}</b> · {{ h.who }}: <s>{{ h.from }}</s> → <b>{{ h.to }}</b>
                <span v-if="h.comment" class="rp-muted">— {{ h.comment }}</span>
              </li>
              <li v-if="!history.length" class="rp-muted">Загружаем…</li>
            </ul>
          </transition>
        </article>
      </transition-group>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { createToaster } from '@meforma/vue-toaster'
import { fetchRules, changeRule, resetRule, fetchRuleHistory } from '@/backend/rulesApi'
import {
  statusTone, formatChangedAt, hourOptions, rangeHint, prepareChange, errorText, countRules, filterRules,
} from '@/pages/admin/rulesPanel.mjs'

const toaster = createToaster()

export default {
  name: 'RulesPanelPage',
  setup() {
    const groups = ref([])
    const canEdit = ref(false)
    const loading = ref(false)
    const saving = ref(false)
    const error = ref('')
    const editing = ref(null)
    const draft = ref({ value: '', comment: '' })
    const formError = ref('')
    const historyOpen = ref(null)
    const history = ref([])
    const query = ref('')

    const shown = computed(() => filterRules(groups.value, query.value))
    const all = computed(() => groups.value.flatMap((g) => g.rules || []))
    const total = computed(() => countRules(groups.value))
    const liveCount = computed(() => all.value.filter((r) => r.status === 'live').length)
    const changedCount = computed(() => all.value.filter((r) => !r.is_default).length)

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const data = await fetchRules()
        groups.value = data.groups || []
        canEdit.value = Boolean(data.can_edit)
      } catch (e) {
        error.value = errorText(e)
      } finally {
        loading.value = false
      }
    }

    const scrollTo = (key) => {
      query.value = ''
      requestAnimationFrame(() => {
        document.getElementById('rules-' + key)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }

    const startEdit = (rule) => {
      editing.value = rule.key
      draft.value = { value: rule.value, comment: '' }
      formError.value = ''
    }
    const cancelEdit = () => { editing.value = null; formError.value = '' }

    const save = async (rule) => {
      const prepared = prepareChange(rule, draft.value.value, draft.value.comment)
      if (prepared.error) { formError.value = prepared.error; return }
      saving.value = true
      try {
        await changeRule(rule.key, prepared.value, prepared.comment)
        toaster.success('«' + rule.title + '» — сохранено')
        editing.value = null
        await load()
      } catch (e) {
        formError.value = errorText(e)
      } finally {
        saving.value = false
      }
    }

    const reset = async (rule) => {
      saving.value = true
      try {
        await resetRule(rule.key, 'Возврат к значению по умолчанию')
        toaster.success('«' + rule.title + '» — снова ' + rule.default_text)
        await load()
      } catch (e) {
        toaster.error(errorText(e))
      } finally {
        saving.value = false
      }
    }

    const toggleHistory = async (rule) => {
      if (historyOpen.value === rule.key) { historyOpen.value = null; return }
      historyOpen.value = rule.key
      history.value = []
      try {
        history.value = await fetchRuleHistory(rule.key)
      } catch (e) {
        toaster.error(errorText(e))
      }
    }

    onMounted(load)

    return {
      groups, shown, query, total, liveCount, changedCount, canEdit, loading, saving, error, editing, draft,
      formError, historyOpen, history, load, scrollTo, startEdit, cancelEdit, save, reset, toggleHistory,
      tone: statusTone, changedAt: formatChangedAt, hours: hourOptions, range: rangeHint,
    }
  },
}
</script>

<style scoped>
.rp { --gold: #f2b705; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df; --brand: #4f46e5;
  padding: 24px; max-width: 1180px; color: var(--ink); }
.rp-hero { position: relative; overflow: hidden; display: flex; justify-content: space-between; gap: 24px;
  align-items: flex-end; padding: 28px 30px; border-radius: 20px; color: #fff;
  background: linear-gradient(135deg, #1f2430 0%, #2b3142 55%, #3b3420 100%); }
.rp-hero__glow { position: absolute; inset: -40% -10% auto auto; width: 420px; height: 420px; border-radius: 50%;
  background: radial-gradient(circle, rgba(242,183,5,.45), rgba(242,183,5,0) 65%); animation: rp-float 9s ease-in-out infinite; }
.rp-hero__text { position: relative; max-width: 680px; }
.rp-eyebrow { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--gold); }
.rp-hero h1 { margin: 6px 0 8px; font-size: 32px; letter-spacing: -.01em; }
.rp-hero p { margin: 0; color: #d6d8de; line-height: 1.5; }
.rp-stats { position: relative; display: flex; gap: 10px; }
.rp-stat { min-width: 88px; padding: 10px 14px; border-radius: 14px; background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.14); backdrop-filter: blur(6px); text-align: center; }
.rp-stat b { display: block; font-size: 24px; color: var(--gold); }
.rp-stat span { font-size: 12px; color: #c9ccd4; }
.rp-toolbar { position: sticky; top: 0; z-index: 2; display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
  margin: 16px 0 6px; padding: 10px 0; background: linear-gradient(#f7f7f5 85%, rgba(247,247,245,0)); }
.rp-search { flex: 1 1 320px; display: flex; align-items: center; gap: 8px; padding: 0 14px; height: 42px;
  border-radius: 12px; background: #fff; border: 1px solid var(--line); transition: box-shadow .2s, border-color .2s; }
.rp-search:focus-within { border-color: var(--gold); box-shadow: 0 0 0 4px rgba(242,183,5,.18); }
.rp-search input { flex: 1; border: 0; outline: 0; font-size: 14px; background: transparent; }
.rp-search__icon { color: var(--muted); font-size: 18px; }
.rp-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.rp-tab { border: 1px solid var(--line); background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 12px;
  cursor: pointer; transition: all .2s; }
.rp-tab:hover { border-color: var(--gold); transform: translateY(-1px); }
.rp-legend { font-size: 12px; color: var(--muted); margin: 4px 0 8px; line-height: 2; }
.rp-group { margin-top: 22px; scroll-margin-top: 70px; }
.rp-group h2 { font-size: 15px; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); margin: 0 0 10px; }
.rp-cards { display: flex; flex-direction: column; gap: 10px; }
.rp-card { display: grid; grid-template-columns: 1fr 250px; gap: 12px 24px; padding: 16px 18px; background: #fff;
  border: 1px solid var(--line); border-radius: 16px; transition: transform .2s, box-shadow .2s, border-color .2s; }
.rp-card:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(31,36,48,.08); }
.rp-card--changed { border-left: 4px solid var(--gold); }
.rp-card--open { box-shadow: 0 12px 34px rgba(31,36,48,.12); }
.rp-card__title { font-weight: 650; font-size: 15px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.rp-card__text { margin: 6px 0 8px; line-height: 1.5; color: #374151; }
.rp-note { margin: 3px 0; font-size: 12px; color: var(--muted); }
.rp-note span { display: inline-block; min-width: 108px; color: #9a9ea8; }
.rp-card__side { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.rp-value { font-size: 30px; font-weight: 750; letter-spacing: -.02em; padding: 2px 14px; border-radius: 12px;
  background: linear-gradient(180deg, #fffaf0, #fff3d1); color: #3b2f05; }
.rp-default { font-size: 12px; color: #a16207; }
.rp-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.rp-btn { padding: 8px 14px; border-radius: 10px; border: 1px solid var(--ink); background: var(--ink); color: #fff;
  font-size: 13px; cursor: pointer; transition: transform .15s, background .2s; }
.rp-btn:hover { transform: translateY(-1px); background: #000; }
.rp-btn--ghost { background: #fff; color: var(--ink); border-color: var(--line); }
.rp-btn--ghost:hover { background: #fffaf0; border-color: var(--gold); }
.rp-btn:disabled { opacity: .55; cursor: default; transform: none; }
.rp-link { background: none; border: 0; color: var(--brand); cursor: pointer; font-size: 12px; padding: 0; }
.rp-form { grid-column: 1 / -1; display: grid; grid-template-columns: 220px 1fr; gap: 10px 16px; padding: 14px;
  border-radius: 12px; background: #fafaf7; border: 1px dashed var(--line); font-size: 12px; color: var(--muted); }
.rp-form label { display: flex; flex-direction: column; gap: 6px; }
.rp-form select, .rp-form input, .rp-form textarea { padding: 8px 10px; border: 1px solid var(--line); border-radius: 10px;
  font-size: 14px; font-family: inherit; color: var(--ink); background: #fff; }
.rp-form .rp-actions, .rp-form .rp-error { grid-column: 1 / -1; justify-content: flex-start; }
.rp-history { grid-column: 1 / -1; margin: 0; padding: 10px 14px; background: #f8f8f6; border-radius: 12px;
  list-style: none; font-size: 13px; }
.rp-history li + li { margin-top: 6px; }
.chip { font-size: 11px; font-weight: 500; padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.chip--live { background: #dcfce7; color: #166534; }
.chip--planned { background: #fef3c7; color: #92400e; }
.chip--fixed { background: #f1f1ef; color: #5b5f69; }
.rp-error { color: #b91c1c; margin: 6px 0; }
.rp-muted { color: var(--muted); }
.rp-small { font-size: 12px; margin: 0; text-align: right; }
.rp-empty { padding: 30px 0; text-align: center; }
.rp-skeleton__row { height: 92px; margin-top: 10px; border-radius: 16px;
  background: linear-gradient(90deg, #f1f1ef 25%, #fafaf8 50%, #f1f1ef 75%); background-size: 200% 100%;
  animation: rp-shimmer 1.2s infinite; }
.rp-card-enter-active, .rp-card-leave-active { transition: all .25s ease; }
.rp-card-enter-from, .rp-card-leave-to { opacity: 0; transform: translateY(8px); }
.rp-slide-enter-active, .rp-slide-leave-active { transition: all .22s ease; }
.rp-slide-enter-from, .rp-slide-leave-to { opacity: 0; transform: translateY(-6px); }
@keyframes rp-shimmer { to { background-position: -200% 0; } }
@keyframes rp-float { 50% { transform: translate(-30px, 20px); } }
@media (prefers-reduced-motion: reduce) {
  .rp-hero__glow, .rp-skeleton__row { animation: none; }
  .rp-card, .rp-btn, .rp-tab { transition: none; }
}
@media (max-width: 760px) {
  .rp { padding: 16px; }
  .rp-hero { flex-direction: column; align-items: flex-start; padding: 22px; }
  .rp-card, .rp-form { grid-template-columns: 1fr; }
  .rp-card__side { align-items: flex-start; }
  .rp-small { text-align: left; }
}
</style>
